import { ArrowUpward } from '@mui/icons-material';
import { Box, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { setSelectedItem, setSortSelectedItem } from 'app/actions/trailer';
import { Autosizer, Scrollbar } from 'app/components';
import { selectedItemTrailerSelector, sortItemsTrailerSelector, sortSelectedItemTrailerSelector } from 'app/selectors/trailer';
import React, { createContext, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';

const TrailerListContext = createContext();

const TrailerList = () => {
  const dispatch = useDispatch();

  const selectedItem = useSelector(selectedItemTrailerSelector);
  const sortSelectedItem = useSelector(sortSelectedItemTrailerSelector);
  const sortItems = useSelector(sortItemsTrailerSelector);

  const handleSortClick = (value) => dispatch(setSortSelectedItem(value));

  const handleItemClick = (value) => dispatch(setSelectedItem(value));

  return (
    <Box
      component='div'
      sx={{
        width: '100%',
        height: '100%',
        '.simplebar-offset > div': {
          '&::-webkit-scrollbar': {
            width: 0,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            boxShadow: 'none',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
        '.simplebar-horizontal': {
          right: '10px',
        },
        '.simplebar-vertical': {
          top: '30px',
          bottom: '0',
        },
      }}>
      <Autosizer>
        {({ height, width }) => (
          <Box sx={{ height, width }}>
            <TrailerListContext.Provider
              value={{
                sortItemClick: handleSortClick,
                sortSelectedItem,
              }}>
              <Scrollbar>
                {({ scrollableNodeRef, contentNodeRef }) => (
                  <FixedSizeList
                    height={height}
                    itemSize={24}
                    overscanCount={10}
                    itemCount={sortItems.length}
                    innerRef={contentNodeRef}
                    outerRef={scrollableNodeRef}
                    innerElementType={TrailerListInnerElementType}
                    itemData={{
                      items: sortItems,
                      onClick: handleItemClick,
                      selectedItem,
                    }}>
                    {TrailerListItem}
                  </FixedSizeList>
                )}
              </Scrollbar>
            </TrailerListContext.Provider>
          </Box>
        )}
      </Autosizer>
    </Box>
  );
};

const TrailerListInnerElementType = forwardRef(({ children, style, sortItem, ...rest }, ref) => (
  <TrailerListContext.Consumer>
    {({ sortItemClick, sortSelectedItem }) => (
      <List
        ref={ref}
        sx={{
          height: style.height,
          minHeight: '100%',
        }}
        {...rest}>
        <ListItem
          disablePadding
          sx={(theme) => ({
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: style.height,
            minHeight: '100%',
            zIndex: '2',
            pointerEvents: 'none',
            padding: theme.spacing(0),
            '.MuiListItemText-root': {
              display: 'flex',
              justifyContent: 'space-between',
              height: '100%',
              position: 'relative',
              flex: 'none',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: '0',
                right: '0',
                height: '100%',
                bgcolor: theme.palette.divider,
                width: '1px',
                zIndex: '1',
                pointerEvents: 'none',
              },
              '&:last-of-type': {
                flex: '1',
                '&:before': {
                  display: 'none',
                },
              },
            },
          })}>
          {config.trailerColumns.map((column) => (
            <ListItemText key={column.name} style={{ width: column.width }} />
          ))}
        </ListItem>
        <ListItem
          disablePadding
          sx={(theme) => ({
            width: '100%',
            height: '30px',
            display: 'flex',
            position: 'sticky !important',
            top: '0',
            zIndex: '1',
            '.MuiListItemButton-root': {
              display: 'flex',
              flex: 'none',
              padding: theme.spacing(0),
              position: 'relative',
              bgcolor: 'background.default',
              borderColor: theme.palette.divider,
              '&:last-of-type': {
                flex: '1',
                '.MuiListItemText-root': {
                  paddingRight: theme.spacing(2),
                },
              },
              '.MuiTouchRipple-rippleVisible': {
                color: 'inherit',
              },
              '.MuiListItemText-root': {
                display: 'flex',
                cursor: 'pointer',
                margin: theme.spacing(0),
                padding: '6px 6px',
                alignItems: 'center',
                justifyContent: 'space-between',
                '.MuiTypography-root': {
                  display: 'flex',
                  '&:nth-of-type(1)': {
                    color: 'text.secondary',
                    marginRight: '4px',
                  },
                  '&:nth-of-type(2) svg': {
                    fontSize: '12px',
                    transition: 'all 300ms',
                    transform: 'rotateZ(0deg)',
                  },
                },
              },
            },
          })}>
          {config.trailerColumns.map((column) => (
            <ListItemButton
              key={column.name}
              onClick={() => sortItemClick(column)}
              disableTouchRipple={!column.order}
              sx={(theme) => ({
                minWidth: column.width,
                '.MuiListItemText-root': {
                  display: 'flex',
                  justifyContent: 'space-between',
                  '.MuiTypography-root': {
                    width: 'auto',
                    '&:nth-of-type(2)': {
                      opacity: '0.2',
                      transform: 'rotateZ(0)',
                      transition: 'transform ' + theme.transitions.duration.shortest + 'ms ' + theme.transitions.easing.easeInOut + ' 0ms',
                      ...(sortSelectedItem?.name === column.name && {
                        opacity: '1',
                        ...(sortSelectedItem?.order === 'DESC' && {
                          transform: 'rotateZ(180deg)',
                        }),
                      }),
                    },
                  },
                },
              })}>
              <ListItemText disableTypography>
                <Typography>{column.label}</Typography>
                <Typography>{column.order && <ArrowUpward fontSize='small' />}</Typography>
              </ListItemText>
            </ListItemButton>
          ))}
        </ListItem>
        {children}
      </List>
    )}
  </TrailerListContext.Consumer>
));

const TrailerListItem = ({ data, index, style }) => {
  const { items, selectedItem, onClick } = data;

  return (
    <ListItem
      key={index}
      disablePadding
      selected={items[index].id === selectedItem?.id}
      onClick={() => onClick(items[index])}
      sx={(theme) => ({
        ...style,
        top: style.top + 30,
        width: '100%',
        userSelect: 'none',
        borderRadius: '0',
        '&:hover': {
          '.MuiListItemText-root': {
            cursor: 'pointer',
          },
        },
        '&.Mui-selected': {
          '.MuiListItemText-root': {
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },
          '.MuiCheckbox-root': {
            color: theme.palette.primary.contrastText,
            '&.Mui-checked': {
              color: theme.palette.primary.contrastText,
            },
          },
        },
        '.MuiListItemText-root': {
          display: 'flex',
          flex: 'none',
          height: '100%',
          margin: theme.spacing(0),
          padding: theme.spacing(0.5, 1),
          alignItems: 'left',
          '&:first-of-type': {
            svg: {
              fontSize: '16px',
              marginRight: '3px',
            },
          },
          '&:last-of-type': {
            flex: '1',
            paddingRight: theme.spacing(2),
          },
          '.MuiTypography-root': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          },
        },
        ...((items.length - (items.length - 1 - index)) % 2 === 0 && {
          '& .MuiListItemText-root': {
            bgcolor: 'background.default',
          },
        }),
      })}>
      {config.trailerColumns.map((column) => (
        <ListItemText key={column.name} style={{ width: column.width }}>
          <Typography>{column.format ? column.format(items[index][column.name]) : items ? items[index][column.name] : ''}</Typography>
        </ListItemText>
      ))}
    </ListItem>
  );
};

export default TrailerList;
